import React from 'react'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Stack,
    MenuItem,
    TextField,
    FormHelperText,
    CircularProgress,
    DialogProps,
    IconButton,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { DatePicker, MobileDateTimePicker } from '@mui/x-date-pickers'
import { formatName, formatStatusCode } from '@edge/spark/utils'
import { SmallLoadingButton, useIsMobile } from '@edge/spark/ui-mui'

import {
    type Agents,
    useCreateAgentContactMutation,
    useGetContactModalInfoQuery,
    useUpdateAgentContactMutation,
    useUserInfoContactModalQuery,
} from '../generated/graphql'
import { createDialog } from '@edge/spark/feature-dialog'
import { ContactTypeSelect } from './ContactTypeSelect'
import { CreatedByInfo } from './CreatedByInfo'

const DIALOG_TITLE = 'Log Contact'

export type ContactModalFormData = {
    agentId: number
    statusCode: string
    typeCode: string
    contactedAt: string
    note: string
    followUpDate: string | null
    completedBy: number | null
    completedAt: string | null
    followUpUserId: number | null
    buyerSellerAccountId?: number | null
}

type SourceDetailPage = 'AGENT' | 'COMPANY' | 'SEARCH' | 'FOLLOWUP'

interface Props {
    open: DialogProps['open']
    onClose: () => void
    accountId?: number
    row: any
    agents: Agents
    fromAgentCard?: boolean
    isEditing?: boolean
    sourceDetailPage?: SourceDetailPage
}

const DEFAULT_STATUS_CODE = 'noaction'

export const ContactModal: React.FC<Props> = ({
    agents,
    onClose,
    open,
    row,
    fromAgentCard,
    isEditing,
    sourceDetailPage,
    accountId,
}: Props) => {
    const { data: currentUserInfo } = useUserInfoContactModalQuery({ fetchPolicy: 'cache-first' })
    const currentUsername = currentUserInfo?.user?.attributes?.authenticationUsername

    const { data: modalData, loading, error: modalError } = useGetContactModalInfoQuery({ fetchPolicy: 'cache-first' })

    const { users } = modalData?.auction || {}
    const { types } = modalData?.auction?.contactManagement || {}
    const { statuses } = modalData?.auction?.contactManagement || {}

    const currentFormUser = users?.find((u) => u?.authenticationUsername === currentUsername)

    const currentFormUserId = currentFormUser?.databaseId || null

    const [statusCode, setStatusCode] = React.useState(row?.statusCode || DEFAULT_STATUS_CODE)
    const [typeCode, setTypeCode] = React.useState(row?.typeCode || '')
    const [contactedAt, setContactedAt] = React.useState<Date | null>(new Date(row?.contactedAt ?? Date.now()))
    const [note, setNote] = React.useState(row?.note || '')
    const [followUpUserId, setFollowUpUserId] = React.useState<number | null>(row?.followUpUserId || currentFormUserId)
    const [followUpDate, setFollowUpDate] = React.useState<Date | null>(
        row?.followUpDate ? new Date(row.followUpDate) : null,
    )
    const [completedBy, setCompletedBy] = React.useState<number | null>(row?.completedBy || currentFormUserId)
    const [completedAt, setCompletedAt] = React.useState<Date | null>(new Date(row?.completedAt ?? Date.now()))
    const mobile = useIsMobile()

    React.useEffect(() => {
        // Since status codes are database driven, the DEFAULT_STATUS_CODE may not be in the list of statuses
        // Only needs to run on when statuses have been loaded

        if (row?.statusCode) {
            // Status code is already set, so default is the existing value
            return
        }

        if (!statuses || !statuses?.length) {
            // Status codes not yet loaded
            return
        }

        if (!statuses?.find((s) => s?.code === DEFAULT_STATUS_CODE)) {
            // Default status code is not in the list of statuses, so set to empty string
            return setStatusCode('')
        }
    }, [statuses, row?.statusCode, setStatusCode])

    // refetchQueries based on current Spark page
    const queriesBySource = (): string[] => {
        switch (true) {
            case sourceDetailPage === 'AGENT':
                return ['getAgentById']
            case sourceDetailPage === 'COMPANY':
                return ['GetContactLogByAccount']
            case sourceDetailPage === 'FOLLOWUP':
                return ['myContactLogFollowups']
            case sourceDetailPage === 'SEARCH':
                return []
            default:
                return []
        }
    }
    const [createAgentContact, { loading: createContactLoading }] = useCreateAgentContactMutation({
        refetchQueries: queriesBySource(),
    })
    const [updateAgentContact, { loading: updateContactLoading }] = useUpdateAgentContactMutation({
        refetchQueries: queriesBySource(),
    })
    const createUpdateContactLoading = createContactLoading || updateContactLoading
    const defaultAgent = agents?.list?.length === 1 ? agents?.list[0]?.agentId : ''
    const [agentId, setAgentId] = React.useState(row?.agentId || defaultAgent)

    const getUserList = (users: any) => {
        return users?.map((user: any) => {
            if (user) {
                return (
                    <MenuItem
                        key={user.databaseId}
                        value={`${user.databaseId || user.databaseId === 0 ? user.databaseId.toString() : ''}`}
                    >
                        {formatName(user)}
                    </MenuItem>
                )
            }
        })
    }

    const disableFormSubmit =
        (!agentId && !accountId) || !statusCode || !typeCode || !contactedAt || createUpdateContactLoading

    const getDateString = (dateArg: any): string | null => {
        if (typeof dateArg !== 'undefined' && dateArg != null) {
            return new Date(dateArg).toISOString()
        }
        return null
    }

    const handleSubmit = async (evt: React.FormEvent): Promise<void> => {
        evt.preventDefault()

        const contactedAtString = getDateString(contactedAt)
        const followUpDateString = getDateString(followUpDate)
        const completedAtString = getDateString(completedAt)

        const params: ContactModalFormData = {
            agentId,
            statusCode,
            typeCode,
            contactedAt: contactedAtString || '',
            note,
            followUpDate: followUpDateString,
            followUpUserId: followUpUserId || currentFormUserId,
            completedAt: completedAtString,
            completedBy: completedBy || currentFormUserId,
            buyerSellerAccountId: accountId ?? null,
        }

        if (params.statusCode === 'followup') {
            params.completedAt = null
            params.completedBy = null
        }

        if (params.statusCode === 'completed') {
            params.followUpUserId = null
            params.followUpDate = null
        }

        if (params.statusCode === 'noaction') {
            params.completedAt = null
            params.completedBy = null
            params.followUpUserId = null
            params.followUpDate = null
        }

        try {
            if (row && (!fromAgentCard || isEditing)) {
                const { id: agentContactId } = row

                const res = await updateAgentContact({
                    variables: {
                        id: agentContactId,
                        ...params,
                    },
                })
                if (res?.data?.updateAgentContact?.id) {
                    onClose()
                    createDialog({
                        message: 'Contact log updated',
                        type: 'SNACKBAR',
                        timeout: 5_000,
                        color: 'success',
                    })
                }
            } else {
                const res = await createAgentContact({ variables: params })
                if (res?.data?.createAgentContact?.id) {
                    onClose()
                    createDialog({
                        message: 'Contact log created',
                        type: 'SNACKBAR',
                        timeout: 5_000,
                        color: 'success',
                    })
                }
            }
        } catch (error) {
            console.error(error)
        }
    }

    const getAgentList = (agents: Agents) => {
        return agents?.list?.reduce(
            (acc: any, agent: any) => {
                if (agent) {
                    acc.push(
                        <MenuItem key={agent.agentId} value={agent.agentId}>
                            {agent.agentName || formatName(agent)}
                        </MenuItem>,
                    )
                }
                return acc
            },
            [<MenuItem value={-1}>No Agent</MenuItem>],
        )
    }

    return (
        <Dialog open={open} onClose={onClose} fullScreen={mobile} maxWidth="sm" fullWidth>
            <DialogTitle>{DIALOG_TITLE}</DialogTitle>
            <IconButton
                onClick={onClose}
                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Stack gap={3}>
                            <ContactTypeSelect
                                value={typeCode}
                                required
                                onChange={(evt, value) => setTypeCode(value)}
                            />
                            <FormControl fullWidth required>
                                <TextField
                                    label="Agent Name"
                                    select
                                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                                        setAgentId(evt.target.value)
                                    }}
                                    disabled={!!row?.agentName}
                                    variant="outlined"
                                    value={agentId}
                                >
                                    {getAgentList(agents)}
                                </TextField>
                                {!agentId && (
                                    <FormHelperText error required>
                                        Required
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl fullWidth>
                                <MobileDateTimePicker
                                    label="Contacted At"
                                    value={contactedAt}
                                    onChange={setContactedAt}
                                    disabled={!!row?.contactedAt}
                                    disableFuture={!isEditing}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Status</FormLabel>
                                <RadioGroup
                                    row
                                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                                        setStatusCode(evt.target.value)
                                    }
                                    value={statusCode}
                                >
                                    {statuses?.map((status, i) => {
                                        return status ? (
                                            <FormControlLabel
                                                key={status.id}
                                                value={typeof status.code === 'string' ? status.code : undefined}
                                                label={formatStatusCode(status.code)}
                                                control={<Radio />}
                                            />
                                        ) : null
                                    })}
                                </RadioGroup>
                                {!statusCode && (
                                    <FormHelperText error required>
                                        Required
                                    </FormHelperText>
                                )}
                            </FormControl>

                            {statusCode && statusCode === 'followup' && (
                                <FormControl fullWidth>
                                    <TextField
                                        label="Assigned To"
                                        select
                                        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                                            const userId = Number(evt.target.value)
                                            if (Number.isInteger(userId)) {
                                                setFollowUpUserId(userId)
                                            }
                                        }}
                                        variant="outlined"
                                        value={
                                            followUpUserId || followUpUserId === 0
                                                ? followUpUserId.toString()
                                                : currentFormUserId || ''
                                        }
                                    >
                                        {getUserList(users)}
                                    </TextField>
                                </FormControl>
                            )}

                            {statusCode && statusCode === 'followup' && (
                                <FormControl fullWidth>
                                    <DatePicker
                                        label="Follow Up Date"
                                        value={followUpDate}
                                        onChange={setFollowUpDate}
                                        disablePast={!isEditing}
                                    />
                                </FormControl>
                            )}

                            {statusCode && statusCode === 'completed' && (
                                <FormControl fullWidth>
                                    <TextField
                                        label="Completed By"
                                        select
                                        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                                            const userId = Number(evt.target.value)
                                            if (Number.isInteger(userId)) {
                                                setCompletedBy(userId)
                                            }
                                        }}
                                        variant="outlined"
                                        value={
                                            completedBy || completedBy === 0
                                                ? completedBy.toString()
                                                : currentFormUserId || ''
                                        }
                                    >
                                        {users?.map((user) => {
                                            return (
                                                user && (
                                                    <MenuItem
                                                        key={user.databaseId}
                                                        value={`${
                                                            user.databaseId || user.databaseId === 0
                                                                ? user.databaseId.toString()
                                                                : ''
                                                        }`}
                                                    >
                                                        {formatName(user)}
                                                    </MenuItem>
                                                )
                                            )
                                        })}
                                    </TextField>
                                </FormControl>
                            )}

                            {statusCode && statusCode === 'completed' && (
                                <FormControl fullWidth>
                                    <MobileDateTimePicker
                                        label="Completed Date"
                                        value={completedAt}
                                        onChange={setCompletedAt}
                                        disableFuture={!isEditing}
                                    />
                                </FormControl>
                            )}

                            <FormControl fullWidth>
                                <TextField
                                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setNote(evt.target.value)}
                                    multiline
                                    label="Notes"
                                    placeholder="Type your note here"
                                    rows="4"
                                    variant="outlined"
                                    value={note}
                                />
                            </FormControl>
                        </Stack>

                        <CreatedByInfo row={row} users={users} isVisible={!!(row?.id || isEditing)} />

                        <DialogActions>
                            <Button onClick={onClose} size="small" disabled={createUpdateContactLoading}>
                                Cancel
                            </Button>
                            <SmallLoadingButton
                                variant="contained"
                                disabled={disableFormSubmit}
                                loading={createUpdateContactLoading}
                                type="submit"
                            >
                                Save
                            </SmallLoadingButton>
                        </DialogActions>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    )
}
